<!-- 
History of a levy collection provider displayed as a series of cards on a timeline.

We only use this style for displaying history for levy collection providers and avoid using
it for other history listings because the timeline is not paged or filtered in any way.
-->
<template>
  <div class="pl-3 pr-3">
    <our-loading-indicator v-if="loading"/>
    <v-timeline 
      v-if="!loading"
      :dense="statusChangesOnly || dense" 
      :style="`max-width:${statusChangesOnly ? '523px' : '100%'}`" 
      align-top
    >
      <history-timeline-card
        v-for="(item, index) in sortedFilteredListing"
        :key="`card-${index}`"
        :item="item"
        :expanded="isExpanded(item.id)"
        :status-changes-only="statusChangesOnly"
        :levy-collection-id="levyCollectionId"
        :provider-id="providerId"
        :levy-collection-name="levyCollectionName"
        :provider-name="providerName"
        @update:expanded="toggleExpanded(item.id)"
      />
    </v-timeline>
    <notice-view-dialog v-model="showNoticeViewDialog" :notice-id="currentNoticeId" />
  </div>
</template>

<script>
import _ from "underscore";
import historyMixins from "@/mixins/history.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import NoticeViewDialog from "@/components/notices/NoticeViewDialog.vue";
import HistoryTimelineCard from "@/components/levy-collection-providers/HistoryTimelineCard.vue";
import VueCodeDiff from "vue-code-diff";

export default {
  components: { NoticeViewDialog, HistoryTimelineCard, VueCodeDiff },
  mixins: [historyMixins, filterMixins, lookupMixins],
  props: {
    providerId: {
      type: Number,
      required: true
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    providerName: {
      type: String,
      required: true
    },
    levyCollectionName: {
      type: String,
      required: true
    },
    ascending: {
      type: Boolean,
      default: true
    },
    statusChangesOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: [],
      showNoticeViewDialog: false,
      currentNoticeId: null
    };
  },
  computed: {
    sortedFilteredListing() {
      var list = _.sortBy(this.state.data, item => {
        return item.date;
      });
      if (!this.ascending) {
        list = list.reverse();
      }
      if (this.statusChangesOnly) {
        list = list.filter(
          item =>
            item.action === "LevyCollectionProviderCreated" || item.action === "LevyCollectionProviderStatusChanged"
        );
      }
      return list;
    },
    state() {
      return this.$store.state.levyCollectionProviders.timeline;
    },
    loaded() {
      return this.state.loaded;
    },
    loading() {
      return this.state.loading;
    },
    hasData() {
      return !!this.state.data.length;
    },
    dense() {
      return this.$vuetify.breakpoint.smAndDown;
    }
  },
  watch: {
    loaded() {
      this.reload();
    },
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    expandAll() {
      this.expanded = [];
      _.each(this.sortedFilteredListing, n => {
        this.expanded.push(n.id);
      });
    },
    collapseAll() {
      this.expanded = [];
    },
    reload() {
      const notLoaded = !this.state.loaded && !this.state.loading;
      const reloadRequired =
        notLoaded || (this.state.levyCollectionId != this.levyCollectionId || this.state.providerId != this.providerId);
      if (reloadRequired) {
        this.$store.dispatch(lcpAction.GET_TIMELINE, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
      }
    },
    isExpanded(id) {
      return _(this.expanded).contains(id);
    },
    toggleExpanded(id) {
      if (this.isExpanded(id)) {
        this.expanded = _(this.expanded).without(id);
      } else {
        this.expanded.push(id);
      }
    }
  }
};
</script>
